/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, useToggle } from "@adapptio/lib-components";
import React, { ChangeEvent, FC, useCallback } from "react";
import { IFieldValidationError } from "../FieldTypes";
import { FieldWrapper } from "../FieldWrapper";
import { Icon, ICON_SIZE, InputAdornment, InputButton } from "../../core";
import { FieldInput } from "../FieldInput";
import { isFunction } from "@adapptio/lib-shared";

export interface IFieldTextProps {
	id?: string;
	label?: string;
	value: string;
	placeholder?: string;
	readOnly?: boolean;
	disabled?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	onChange?: (value: string, ev: ChangeEvent<HTMLInputElement>) => void;
	onFocus?: React.FocusEventHandler<HTMLInputElement>;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
	onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;
	onPressEnter?: (value: string, ev: ChangeEvent<HTMLInputElement>) => void;
	minLength?: number;
	maxLength?: number;
	grow?: boolean;
	inputRef?: React.MutableRefObject<HTMLInputElement>;
	autoFocus?: boolean;
	autoFocusSelect?: boolean;
	type?: React.HTMLProps<HTMLInputElement>["type"];
	debounceTimeout?: number;
	multiLine?: boolean;
	maxRows?: number;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	clearable?: boolean;
}

export const FieldText: FC<IFieldTextProps> = ({
	id,
	value,
	placeholder,
	readOnly,
	disabled,
	validationErrors,
	className,
	grow,
	onFocus,
	onBlur,
	onChange,
	onKeyDown,
	onKeyUp,
	onPressEnter,
	inputRef,
	minLength,
	maxLength,
	autoFocus,
	autoFocusSelect,
	type,
	debounceTimeout,
	multiLine,
	maxRows,
	startAdornment,
	endAdornment,
	clearable
}) => {
	const baseClass = BEM("edt-field-text");
	const [ passwordVisible, togglePasswordVisibility ] = useToggle(false);
	const isPasswordField = type === "password";

	const displayedType = isPasswordField && passwordVisible ? "text" : type;

	const adornment = isPasswordField || clearable;

	const handleChange = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => {
			if (onChange) {
				onChange(ev.target.value, ev);
			}
		},
		[ onChange ]
	);

	const handleKeyUp = useCallback(
		(ev: React.KeyboardEvent<HTMLInputElement>) => {
			if (ev.key === "Enter" && onPressEnter) {
				onPressEnter(ev.currentTarget.value, ev as unknown as ChangeEvent<HTMLInputElement>);
			}

			if (onKeyUp) {
				onKeyUp(ev);
			}
		},
		[ onPressEnter, onKeyUp ]
	);

	const handleClearClick = useCallback(() => {
		if (isFunction(onChange)) {
			onChange(null, null);
		}
	}, [ onChange ]);

	return (
		<FieldWrapper type="text" className={className} grow={grow}>
			<FieldInput
				id={id}
				className={baseClass.toClassName()}
				type={displayedType}
				value={value || ""}
				placeholder={placeholder}
				onChange={handleChange}
				onFocus={onFocus}
				onBlur={onBlur}
				onKeyDown={onKeyDown}
				onKeyUp={handleKeyUp}
				inputRef={inputRef}
				minLength={minLength}
				maxLength={maxLength}
				autoFocus={autoFocus}
				autoFocusSelect={autoFocusSelect}
				readOnly={readOnly}
				disabled={disabled}
				debounceTimeout={debounceTimeout}
				multiLine={multiLine}
				maxRows={maxRows}
				errors={validationErrors}
				startAdornment={startAdornment}
				endAdornment={
					endAdornment ? (
						endAdornment
					) : adornment ? (
						<InputAdornment>
							{clearable && (
								<InputButton
									className={baseClass.element("editor-clear").toClassName()}
									onClick={handleClearClick}
									tabIndex={-1}
									disabled={!value}
								>
									<Icon
										className={baseClass.element("editor-clear-icon").toClassName()}
										name="mdi/close"
									/>
								</InputButton>
							)}
							{isPasswordField && (
								<InputButton type="button" onClick={togglePasswordVisibility}>
									<Icon
										name={passwordVisible ? "mdi/eye-off" : "mdi/eye"}
										size={ICON_SIZE.XSMALL}
									/>
								</InputButton>
							)}
						</InputAdornment>
					) : null
				}
			/>
		</FieldWrapper>
	);
};

FieldText.displayName = "FieldText";
